import { useEffect, useMemo, useState } from 'react'

import merchantApi from '../../services/merchantApi'
import './PaymentConfigPage.scss'
import Header from '../../components/Header/Header'
import { Icon } from '@iconify/react/dist/iconify.js'
import { useNavigate } from 'react-router-dom'
import IconSelect from '../../components/IconSelect/IconSelect'
import { Divider, Grid } from '@mui/material'

const moduleCode = 'payment_config'

const ICON_PAYMENT = {
    qrcode: 'ph:qr-code',
    ewallet: 'ph:qr-code',
    card: 'ph:credit-card',
    fpx: 'ph:bank',
    pay_at_counter: 'ph:cash-register',
}

const PAYMENT_METHOD_LIST = [
    {
        "id": 7,
        "name": "E-wallets",
        "description": "Touch 'n Go, Grabpay, Boost",
        "code": "ewallet",
        "gateway_id": 3,
        "gateway_code": "fiuu",
    },
    {
        "id": 8,
        "name": "Credit / Debit Card",
        "description": "Visa, Mastercard supported",
        "code": "card",
        "gateway_id": 3,
        "gateway_code": "fiuu",
    },
    {
        "id": 9,
        "name": "FPX",
        "description": "FPX online banking",
        "code": "fpx",
        "gateway_id": 3,
        "gateway_code": "fiuu",
    },
    {
        "id": 10,
        "name": "Pay at Counter",
        "description": "Dine-in only",
        "code": "pay_at_counter",
        "gateway_id": 1,
        "gateway_code": "manual",
    }
]

const KIOSK_PAYMENT_METHOD_LIST = [
    {
        "id": 1,
        "name": "Credit / Debit Card",
        "description": "Pay at this kiosk using the connected card terminal.",
        "code": "card",
        "gateway_id": 3,
        "gateway_code": "fiuu",
    },
    {
        "id": 2,
        "name": "QR Code",
        "description": "Pay at this kiosk using a QR code.",
        "code": "qrcode",
        "gateway_id": 3,
        "gateway_code": "fiuu",
    },
]

const MODE_OPTIONS = [
    { value: 'sandbox', label: 'Sandbox' },
    { value: 'production', label: 'Production' },
]

export default function PaymentConfigPage() {
    const [config, setConfig] = useState(PAYMENT_METHOD_LIST)
    const [gateways, setGateways] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [userInfo, setUserInfo] = useState('')
    const [fieldError, setFieldError] = useState('')
    const [currentOutletId, setCurrentOutletId] = useState('')
    const [customerFiuuConfig, setCustomerFiuuConfig] = useState({})
    const [kioskFiuuConfig, setKioskFiuuConfig] = useState({})
    const [manualConfig, setManualConfig] = useState({})

    const navigate = useNavigate()

    useEffect(() => {
        let mounted = true

        loadUserData()

        return () => {
            mounted = false
        }
    }, [])

    useEffect(() => {
        if (currentOutletId) {
            loadPaymentConfig()
        }
    }, [currentOutletId])

    const normalizePaymentConfig = (data) => {
        const customerConfig = data?.customer
        const kioskConfig = data?.kiosk

        const manualConfig = customerConfig?.gateways?.find((gateway) => gateway.code === 'manual') || {}
        console.log("manualConfig:", manualConfig)
        setManualConfig(manualConfig)

        const customerFiuuConfig = customerConfig?.gateways?.find((gateway) => gateway.code === 'fiuu') || {}

        const normalizedCustomerConfig = {
            merchant_id: customerFiuuConfig?.merchant_id || '',
            merchant_key: customerFiuuConfig?.merchant_key || '',
            mode: customerFiuuConfig?.mode || '',
            verify_key: customerFiuuConfig?.verify_key || '',

            payment_methods: PAYMENT_METHOD_LIST.map((method) => {
                const methodConfig = customerConfig?.payment_methods?.find((m) => m.code === method.code)
                return {
                    ...method,
                    status: methodConfig?.status || false,
                }
            }),
        }
        setCustomerFiuuConfig(normalizedCustomerConfig)
        // For kiosk config, we need to map the payment methods and kiosks
        const kioskFiuuConfig = kioskConfig?.gateways?.find((gateway) => gateway.code === 'fiuu_cloud_ecr')?.config || {}
        setKioskFiuuConfig({
            ...kioskFiuuConfig,
            kiosks: kioskConfig?.kiosks ? kioskConfig?.kiosks?.map((kiosk) => ({
                ...kiosk,
                payment_methods: KIOSK_PAYMENT_METHOD_LIST.map((method) => ({
                    ...method,
                    status: kioskConfig?.payment_methods?.find((kioskConfig) => kioskConfig.code === method.code)?.config?.kiosk?.find((kioskConfig) => kioskConfig.kiosk_id === kiosk.id)?.status || false,
                })),
                id: kiosk.id,
                pos_id: kiosk.pos_id || '',
            })) : [],
        })
    }
    console.log("customerFiuuConfig:", customerFiuuConfig)
    console.log("kioskFiuuConfig:", kioskFiuuConfig)
    const loadPaymentConfig = async () => {
        setLoading(true)
        setError('')

        try {
            const response = await merchantApi.paymentConfig.configs({ outlet_id: currentOutletId })
            console.log("Payment config response:", response)
            normalizePaymentConfig(response?.data)

        } catch (err) {
            setError(err.message || 'Unable to load payment configuration.')
        } finally {
            setLoading(false)
        }
    }

    const loadUserData = async () => {
        try {
            const response = await merchantApi.auth.me();
            console.log("User info response:", response?.data?.default_outlet?.id)
            setCurrentOutletId(response?.data?.default_outlet?.id || null)
            if (!response?.data?.permissions?.includes(`${moduleCode}-setting`)) {
                navigate('/unauthorized')
                return
            }
            setUserInfo(response?.data || null)
        } catch (error) {
            setError(error.message || 'Error fetching data.')
        }
    }

    const updateMethod = (id, checked) => {
        let methodList = [...customerFiuuConfig?.payment_methods]
        const methodIndex = methodList.findIndex((method) => method.id === id)
        if (methodIndex !== -1) {
            methodList[methodIndex].status = checked
            setCustomerFiuuConfig({
                ...customerFiuuConfig,
                payment_methods: methodList
            })
        }
    }

    const updateCustomerFiuuConfig = (id, field, value) => {
        let newCustomerFiuuConfig = { ...customerFiuuConfig }
        newCustomerFiuuConfig[field] = value
        setCustomerFiuuConfig(newCustomerFiuuConfig)
    }

    const updateKioskFiuuConfig = (id, field, value, methodId, kioskId) => {
        let newKioskFiuuConfig = { ...kioskFiuuConfig }
        if (field === 'payment_methods' && methodId && kioskId) {
            const kioskIndex = newKioskFiuuConfig?.kiosks?.findIndex((kiosk) => kiosk.id === kioskId)
            if (kioskIndex !== -1) {
                const methodIndex = newKioskFiuuConfig?.kiosks[kioskIndex]?.payment_methods?.findIndex((method) => method.id === methodId)
                if (methodIndex !== -1) {
                    newKioskFiuuConfig.kiosks[kioskIndex].payment_methods[methodIndex].status = value
                }
            }
        } else if((field === 'pos_id' || field === 'device_id') && kioskId) {
            const kioskIndex = newKioskFiuuConfig?.kiosks?.findIndex((kiosk) => kiosk.id === kioskId)
            if (kioskIndex !== -1) {
                newKioskFiuuConfig.kiosks[kioskIndex][field] = value
            }
        } else {
            newKioskFiuuConfig[field] = value
        }
        setKioskFiuuConfig(newKioskFiuuConfig)
    }

    const handleSave = async () => {
        setSaving(true)
        setError('')
        setSuccess('')

        const configPayload = {
            customer: {
                gateways: [
                    {
                        code: 'fiuu',
                        config: {
                            mode: customerFiuuConfig?.mode || '',
                            merchant_id: customerFiuuConfig?.merchant_id || '',
                            merchant_key: customerFiuuConfig?.merchant_key || '',
                            verify_key: customerFiuuConfig?.verify_key || ''
                        },
                        status: true
                    },
                    { code: 'manual', status: manualConfig?.status || false }
                ],
                payment_methods: customerFiuuConfig?.payment_methods?.map((method) => ({
                    // id: method.id,
                    code: method.code,
                    gateway_code: method.gateway_code,
                    status: method.status || false
                }))
            },
            kiosk: {
                gateways: [
                    {
                        code: 'fiuu_cloud_ecr', status: true, config: {
                            environment: kioskFiuuConfig?.environment || '',
                            merchant_id: kioskFiuuConfig?.merchant_id || '',
                            secret_key: kioskFiuuConfig?.secret_key || '',
                        }
                    }
                ],
                kiosks: kioskFiuuConfig?.kiosks?.filter((kiosk) => kiosk.id).map((kiosk) => ({
                    id: kiosk.id,
                    pos_id: kiosk.pos_id || '',
                    device_id: kiosk.device_id || '',
                    payment_methods: kiosk?.payment_methods?.map((method) => ({
                        // id: method.id,
                        code: method.code,
                        status: method.status || false
                    }))
                }))
            },
        }



        try {
            console.log("Saving config payload:", configPayload)
            const response = await merchantApi.paymentConfig.updateConfigs(configPayload)
            setSuccess('Payment configuration saved successfully.')

            await loadPaymentConfig()
            setSuccess('Payment gateway configuration saved successfully.')
            setError('')
            setFieldError('')
        } catch (err) {
            setError(err.message || 'Unable to save payment configuration.')
            setFieldError(err?.data?.errors || '')
        } finally {
            setSaving(false)
        }
    }
    return (
        <div className="payment-config-page">
            <Header
                items={[
                    { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
                    { label: 'Payment config', current: true },
                ]}
            />
            <div className="payment-page-header">
                <div>
                    <h1>Payment configuration</h1>

                    <p>Payment methods, gateway and tax</p>
                </div>

                <button
                    className="btn-primary"
                    disabled={saving || loading}
                    onClick={handleSave}
                    type="button"
                >
                    {saving ? 'Saving...' : 'Save changes'}
                </button>
            </div>

            {loading && <div className="payment-alert">Loading payment configuration...</div>}
            {error && <div className="payment-alert payment-alert--error">{error}</div>}
            {success && <div className="payment-alert payment-alert--success">{success}</div>}
            <Grid container spacing={2}>
                <Grid item sm={6} xs={12} display="flex" flexDirection="column" gap={2}>
                    <div className="payment-card">
                        <div className="payment-card__header">
                            <h2>Fiuu (For Customer)</h2>
                        </div>

                        <div className="gateway-form">
                            <div className="form-group">
                                <label>Mode</label>

                                <IconSelect
                                    value={customerFiuuConfig?.mode || ''}
                                    onChange={(e) => updateCustomerFiuuConfig('customer', 'mode', e.target.value)}
                                    options={MODE_OPTIONS}
                                />
                                <div className="field-error">{fieldError?.[`customer.gateways.0.config.mode`]}</div>
                            </div>
                            <div className="form-group">
                                <label>Merchant id</label>

                                <input
                                    disabled={loading || saving}
                                    onChange={(event) => updateCustomerFiuuConfig('customer', 'merchant_id', event.target.value)}
                                    placeholder="Enter merchant id"
                                    type="text"
                                    value={customerFiuuConfig?.merchant_id || ''}
                                />
                                <div className="field-error">{fieldError?.[`customer.gateways.0.config.merchant_id`]}</div>
                            </div>

                            <div className="form-group">
                                <label>Merchant key</label>

                                <input
                                    disabled={loading || saving}
                                    onChange={(event) => updateCustomerFiuuConfig('customer', 'merchant_key', event.target.value)}
                                    placeholder="Enter merchant key"
                                    type="password"
                                    value={customerFiuuConfig?.merchant_key || ''}
                                />
                                <div className="field-error">{fieldError?.[`customer.gateways.0.config.secret_key`]}</div>
                            </div>
                            <div className="form-group">
                                <label>Verify key</label>

                                <input
                                    disabled={loading || saving}
                                    onChange={(event) => updateCustomerFiuuConfig('customer', 'verify_key', event.target.value)}
                                    placeholder="Enter verify key"
                                    type="password"
                                    value={customerFiuuConfig?.verify_key || ''}
                                />
                                <div className="field-error">{fieldError?.[`customer.gateways.0.config.verify_key`]}</div>
                            </div>
                        </div>

                        <div className="payment-methods">
                            <div className="kiosk-item-card--header">Payment methods</div>
                            {customerFiuuConfig?.payment_methods?.length > 0 ? customerFiuuConfig?.payment_methods.map((method, index) => (
                                <div className="payment-method" key={method.id}>
                                    <div className="payment-method__left">
                                        <div className="payment-method__icon">
                                            <Icon icon={method.code in ICON_PAYMENT ? ICON_PAYMENT[method.code] : ''} height={16} />
                                        </div>

                                        <div>
                                            <div className='payment-card__method-title'>{method.name}</div>
                                            <p>{method.description}</p>
                                        </div>
                                    </div>

                                    <label className="switch">
                                        <input
                                            checked={method.status}
                                            disabled={loading || saving}
                                            onChange={(event) => updateMethod(method.id, event.target.checked)}
                                            type="checkbox"
                                        />

                                        <span className="slider" />
                                    </label>
                                </div>
                            )) : <div className="payment-alert">No payment methods available.</div>}
                        </div>
                    </div>
                    <div className="payment-card">
                        <div className="payment-card__header">
                            <h2>Pay At Counter</h2>
                        </div>
                        <div className="payment-methods">
                            <div className="form-group form-group__switch">
                                <label>Status</label>
                                <label className="switch">
                                    <input
                                        checked={manualConfig.status}
                                        disabled={loading || saving}
                                        onChange={(event) => setManualConfig({ ...manualConfig, status: event.target.checked })}
                                        type="checkbox"
                                    />

                                    <span className="slider" />
                                </label>
                            </div>
                        </div>
                    </div>
                </Grid>
                <Grid item sm={6} xs={12}>
                    <div className="payment-card">
                        <div className="payment-card__header">
                            <h2>CloudECR (For Kiosk)</h2>
                        </div>

                        <div className="gateway-form">
                            <div className="form-group">
                                <label>Env</label>
                                <input
                                    disabled={loading || saving}
                                    onChange={(event) => updateKioskFiuuConfig('kiosk', 'environment', event.target.value)}
                                    placeholder="Enter env"
                                    type="text"
                                    value={kioskFiuuConfig?.environment || ''}
                                />
                                <div className="field-error">{fieldError?.[`kiosk.gateways.0.config.environment`]}</div>
                            </div>
                            <div className="form-group">
                                <label>Merchant ID</label>
                                <input
                                    disabled={loading || saving}
                                    onChange={(event) => updateKioskFiuuConfig('kiosk', 'merchant_id', event.target.value)}
                                    placeholder="Enter merchant id"
                                    type="text"
                                    value={kioskFiuuConfig?.merchant_id || ''}
                                />
                                <div className="field-error">{fieldError?.[`kiosk.gateways.0.config.merchant_id`]}</div>
                            </div>

                            <div className="form-group">
                                <label>Secret key</label>
                                <input
                                    disabled={loading || saving}
                                    onChange={(event) => updateKioskFiuuConfig('kiosk', 'secret_key', event.target.value)}
                                    placeholder="Enter secret key"
                                    type="password"
                                    value={kioskFiuuConfig?.secret_key || ''}
                                />
                                <div className="field-error">{fieldError?.[`kiosk.gateways.0.config.secret_key`]}</div>
                            </div>
                        </div>

                        <div className="payment-methods">
                            {
                                kioskFiuuConfig?.kiosks?.length === 0 && <div className="payment-alert">No kiosks available.</div>
                            }
                            {
                                kioskFiuuConfig?.kiosks?.length > 0 && kioskFiuuConfig?.kiosks?.map((kiosk, index) => (
                                    <div className="kiosk-item-card">
                                        <div className="kiosk-item-card--header">{kiosk?.name || '-'} ({kiosk?.code || '-'})</div>
                                        <div className="form-group">
                                            <label>POS ID</label>

                                            <input
                                                disabled={loading || saving}
                                                onChange={(event) => updateKioskFiuuConfig('kiosk', 'pos_id', event.target.value, null, kiosk.id)}
                                                placeholder="Enter POS ID"
                                                type="text"
                                                value={kiosk?.pos_id || ''}
                                            />
                                            <div className="field-error">{fieldError?.[`kiosk_fiuu.pos_id`]}</div>
                                        </div>
                                        <div className="form-group">
                                            <label>Device ID</label>

                                            <input
                                                disabled={loading || saving}
                                                onChange={(event) => updateKioskFiuuConfig('kiosk', 'device_id', event.target.value, null, kiosk.id)}
                                                placeholder="Enter Device ID"
                                                type="text"
                                                value={kiosk?.device_id || ''}
                                            />
                                            <div className="field-error">{fieldError?.[`kiosk_fiuu.device_id`]}</div>
                                        </div>
                                        <Divider className="kiosk-item-card--divider" />
                                        <div className="kiosk-item-card--header">Payment methods</div>
                                        {
                                            kiosk?.payment_methods?.length === 0 && <div className="payment-alert">No payment methods available.</div>
                                        }
                                        {
                                            kiosk?.payment_methods?.length > 0 && kiosk?.payment_methods?.map((method, index) => (
                                                <div className="payment-method" key={method.id}>
                                                    <div className="payment-method__left">
                                                        <div className="payment-method__icon">
                                                            <Icon icon={method.code in ICON_PAYMENT ? ICON_PAYMENT[method.code] : ''} height={16} />
                                                        </div>

                                                        <div>
                                                            <div className='payment-card__method-title'>{method.name}</div>
                                                            <p>{method.description}</p>
                                                        </div>
                                                    </div>

                                                    <label className="switch">
                                                        <input
                                                            checked={method.status}
                                                            disabled={loading || saving}
                                                            onChange={(event) => updateKioskFiuuConfig('kiosk', 'payment_methods', event.target.checked, method.id, kiosk.id)}
                                                            type="checkbox"
                                                        />

                                                        <span className="slider" />
                                                    </label>
                                                </div>
                                            ))
                                        }
                                    </div>
                                ))
                            }

                        </div>
                    </div>
                </Grid>
            </Grid>
        </div>
    )
}
